/**
 * Router type definitions for Lytstore
 * Defines route meta types for both shopper and admin routes
 */

import type { RouteLocationNormalized } from 'vue-router'

/**
 * Extended route meta for all routes
 */
export interface RouteMeta {
  /** Whether the route requires authentication */
  requiresAuth?: boolean
  /** Whether the route requires store admin access (legacy - use requiresStaffAuth) */
  requiresStoreAdmin?: boolean
  /** Whether the route requires federated staff authentication from Lytning */
  requiresStaffAuth?: boolean
  /** Page title for document title and breadcrumbs */
  title?: string
  /** Breadcrumb text (defaults to title if not specified) */
  breadcrumb?: string
  /** Icon for admin sidebar highlighting */
  icon?: string
  /** Whether to hide from navigation */
  hideFromNav?: boolean
}

/**
 * Admin route params structure
 */
export interface AdminRouteParams {
  slug: string
  orderId?: string
}

/**
 * Shopper route params for store-related pages
 */
export interface ShopperStoreRouteParams {
  storeSlug?: string
  slug?: string
  id?: string
}

/**
 * Type guard to check if route is an admin route
 */
export function isAdminRoute(route: RouteLocationNormalized): boolean {
  return route.path.startsWith('/admin/')
}

/**
 * Type guard to check if route requires authentication
 */
export function requiresAuthentication(route: RouteLocationNormalized): boolean {
  return route.matched.some(record => record.meta?.requiresAuth === true)
}

/**
 * Type guard to check if route requires store admin access
 */
export function requiresStoreAdmin(route: RouteLocationNormalized): boolean {
  return route.matched.some(record => record.meta?.requiresStoreAdmin === true)
}

// Extend vue-router module to include our custom meta types
declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    requiresStoreAdmin?: boolean
    requiresStaffAuth?: boolean
    title?: string
    breadcrumb?: string
    icon?: string
    hideFromNav?: boolean
  }
}
